import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Send, MessageSquare, CheckCircle2, Clock } from "lucide-react";

const CATEGORIES = [
  { id: "general", label: "General" },
  { id: "technical", label: "Technical issue" },
  { id: "billing", label: "Billing & payments" },
  { id: "safety", label: "Safety & moderation" },
  { id: "account", label: "Account" },
];

const STATUS_STYLE = {
  open: "text-amber-300 border-amber-500/40",
  answered: "text-emerald-300 border-emerald-500/40",
  closed: "text-text-muted border-white/15",
};

export default function Support() {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [tickets, setTickets] = useState(null);

  const load = () => api.get("/support/tickets/mine").then(({ data }) => setTickets(data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || message.trim().length < 5) {
      toast.error("Add a subject and a short description"); return;
    }
    setSending(true);
    try {
      await api.post("/support/tickets", { subject, category, message });
      toast.success("Ticket submitted — our team will be in touch");
      setSubject(""); setMessage(""); setCategory("general");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit ticket");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/15 py-3 focus:border-wine outline-none transition-colors";

  return (
    <div className="pt-24 pb-24 max-w-3xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2"><LifeBuoy size={14} /> Help & Support</p>
        <h1 className="font-display text-6xl font-light">How can we help?</h1>
        <p className="text-text-muted mt-4">Send us a message and the Velluto concierge team will get back to you, usually within a day.</p>
      </motion.div>

      <form onSubmit={submit} data-testid="support-form" className="rounded-3xl border border-white/10 bg-surface p-8 space-y-6">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-text-muted mb-3 block">Subject</label>
          <input data-testid="support-subject" className={field} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Briefly, what's this about?" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-text-muted mb-3 block">Category</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button type="button" key={c.id} data-testid={`support-cat-${c.id}`} onClick={() => setCategory(c.id)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] border transition-colors ${category === c.id ? "bg-wine border-wine text-white" : "border-white/15 text-text-muted hover:text-text-main"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-text-muted mb-3 block">Message</label>
          <textarea data-testid="support-message" rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-transparent border border-white/15 rounded-2xl p-4 focus:border-wine outline-none transition-colors" placeholder="Tell us what's happening…" />
        </div>
        <button data-testid="support-submit" disabled={sending}
          className="flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors disabled:opacity-50">
          <Send size={15} /> {sending ? "Sending…" : "Submit ticket"}
        </button>
      </form>

      <div className="mt-14">
        <h2 className="font-display text-3xl font-light mb-6 flex items-center gap-3"><MessageSquare size={20} className="text-gold" /> Your tickets</h2>
        {tickets === null ? <p className="text-text-muted">Loading…</p> :
         tickets.length === 0 ? <p data-testid="no-my-tickets" className="text-text-muted italic">No tickets yet — you're all good.</p> :
         <div className="space-y-4">
           {tickets.map((t) => (
             <div key={t.id} data-testid={`my-ticket-${t.id}`} className="rounded-2xl border border-white/10 bg-surface p-5">
               <div className="flex items-center justify-between gap-3">
                 <h3 className="text-lg">{t.subject}</h3>
                 <span className={`text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 rounded-full flex items-center gap-1 ${STATUS_STYLE[t.status] || STATUS_STYLE.open}`}>
                   {t.status === "answered" ? <CheckCircle2 size={11} /> : <Clock size={11} />} {t.status}
                 </span>
               </div>
               <p className="text-sm text-text-muted mt-2">{t.message}</p>
               {t.replies?.length > 0 && (
                 <div className="mt-4 space-y-3 border-t border-white/8 pt-4">
                   {t.replies.map((r, i) => (
                     <div key={i} className="rounded-xl bg-wine/10 border border-wine/30 p-3">
                       <p className="text-[10px] uppercase tracking-[0.15em] text-gold mb-1">{r.admin_name || "Velluto Support"}</p>
                       <p className="text-sm">{r.message}</p>
                     </div>
                   ))}
                 </div>
               )}
             </div>
           ))}
         </div>}
      </div>
    </div>
  );
}
